import React, { useState, useEffect, useCallback } from 'react';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot, ReferenceLine 
} from 'recharts';
import { analyzeRiskScenario } from '../services/geminiService';
import { fetchBatchPrices } from '../services/MarketDataService';
import { calculateBlackScholes, generateSensitivityMatrix, MatrixCell } from '../services/mathEngine';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import { 
    IconSearch, IconQuant, IconOps, IconStrategy, IconActivity, IconTrendingUp 
} from '../components/Icons';
import toast from 'react-hot-toast';

interface QuantPricingViewProps {
    onBookQuote?: (quoteData: any) => void;
}

const useDebounce = (value: any, delay: number) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const QuantPricingView: React.FC<QuantPricingViewProps> = ({ onBookQuote }) => {
  const { currentUser } = useAuth();
  
  // --- TABS & MODES ---
  const [activeTab, setActiveTab] = useState<'pricing' | 'matrix' | 'sabr'>('pricing');
  const [calculationMode, setCalculationMode] = useState<'price' | 'iv'>('price');
  
  // --- INPUTS ---
  const [productType, setProductType] = useState('European Option');
  const [optionRight, setOptionRight] = useState<'Call' | 'Put'>('Call');
  const [underlying, setUnderlying] = useState('NVDA'); 
  
  const [spot, setSpot] = useState(135.0);
  const [strike, setStrike] = useState(140);
  const [vol, setVol] = useState(45); 
  const [marketPrice, setMarketPrice] = useState(5.50); 
  const [rate, setRate] = useState(4.5);
  const [time, setTime] = useState(30); 

  // --- SABR INPUTS ---
  const [sabrAlpha, setSabrAlpha] = useState(0.25);
  const [sabrBeta, setSabrBeta] = useState(1.0);
  const [sabrRho, setSabrRho] = useState(-0.6);
  const [sabrNu, setSabrNu] = useState(0.4);

  // --- DEBOUNCED INPUTS ---
  const dSpot = useDebounce(spot, 300);
  const dStrike = useDebounce(strike, 300);
  const dTime = useDebounce(time, 300);
  const dVol = useDebounce(vol, 300);
  const dRate = useDebounce(rate, 300);
  const dOptionRight = useDebounce(optionRight, 300);
  const dAlpha = useDebounce(sabrAlpha, 100);
  const dBeta = useDebounce(sabrBeta, 100);
  const dRho = useDebounce(sabrRho, 100);
  const dNu = useDebounce(sabrNu, 100);

  // --- RESULTS ---
  const [pricingResult, setPricingResult] = useState<any>(null);
  const [ivResult, setIvResult] = useState<any>(null);
  const [volSurfaceData, setVolSurfaceData] = useState<any[]>([]);
  const [sabrData, setSabrData] = useState<any[]>([]);
  const [scenarioMatrix, setScenarioMatrix] = useState<MatrixCell[][] | null>(null);
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isFetchingPrice, setIsFetchingPrice] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState('');

  // 1. HELPERS
  const generateVolSurface = (atmVol: number) => {
      const surface = [];
      const range = spot * 0.4;
      for (let k = spot - range; k <= spot + range; k += (range / 10)) {
          const moneyness = Math.log(k / spot);
          const skew = -0.3 * moneyness + 1.5 * moneyness * moneyness; 
          surface.push({
              strike: Math.round(k),
              iv: (atmVol + (skew * 100)).toFixed(1)
          });
      }
      return surface;
  };

  const handleTickerSearch = async () => {
      if (!underlying || !currentUser) return;
      setIsFetchingPrice(true);
      try {
          const token = await currentUser.getIdToken();
          const prices = await fetchBatchPrices([underlying.toUpperCase()], token);
          const price = prices[underlying.toUpperCase()];
          if (price) {
              setSpot(price);
              toast.success(`Updated Spot: $${price}`);
              if (strike === 140) setStrike(Math.round(price / 5) * 5); 
          } else {
              toast.error("Price not found");
          }
      } catch (e) {
          console.error("Price fetch failed", e);
      } finally {
          setIsFetchingPrice(false);
      }
  };

  // 2. MAIN CALCULATION
  const calculatePrice = useCallback(async () => {
    if (!currentUser) return;
    if (activeTab !== 'pricing') return;

    setIsAnalyzing(true);
    setAiAnalysis('');
    
    try {
        const token = await currentUser.getIdToken();

        // A. PRICING MODE
        if (calculationMode === 'price') {
            setIvResult(null);
            
            const surface = generateVolSurface(vol);
            setVolSurfaceData(surface);

            try {
                const res = await fetch(`${API_BASE_URL}/aiq/price-option`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        spot: dSpot, strike: dStrike, time: dTime, vol: dVol, rate: dRate, type: dOptionRight
                    })
                });
                const data = await res.json();
                
                if (data.error) throw new Error(data.error);
                setPricingResult(data);
                
                if (!pricingResult) {
                    const prompt = `Analyze risk for ${underlying} ${dOptionRight} Strike ${dStrike} (Spot ${dSpot}). Vol ${dVol}%. Delta ${data.greeks?.delta}.`;
                    const analysis = await analyzeRiskScenario(prompt, { product: 'Option', underlying });
                    setAiAnalysis(analysis);
                }
            } catch (e) {
                console.warn("Backend pricing failed, using local fallback");
                const local = calculateBlackScholes(dSpot, dStrike, dTime/365, dRate/100, dVol/100, dOptionRight.toLowerCase() as any);
                setPricingResult(local);
            }
        } 
        
        // B. IMPLIED VOL MODE
        else if (calculationMode === 'iv') {
            setPricingResult(null);
            try {
                const res = await fetch(`${API_BASE_URL}/aiq/calculate-iv`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({
                        spot: dSpot, strike: dStrike, time: dTime, rate: dRate, marketPrice, type: dOptionRight
                    })
                });
                const data = await res.json();
                
                setIvResult(data);
                if(data.impliedVol) setVolSurfaceData(generateVolSurface(data.impliedVol * 100));

                const prompt = `IV for ${underlying} is ${data.percent}%. Is this high?`;
                const analysis = await analyzeRiskScenario(prompt, { product: 'Option', iv: data.percent });
                setAiAnalysis(analysis);

            } catch (e) {}
        }

    } catch (e) {
        console.error(e);
    } finally {
        setIsAnalyzing(false);
    }
  }, [currentUser, dSpot, dStrike, dTime, dVol, dRate, dOptionRight, marketPrice, activeTab, calculationMode]);

  useEffect(() => { calculatePrice(); }, [calculatePrice]);

  // 3. SABR CALCULATION
  const calculateSabr = useCallback(async () => {
    if (!currentUser || activeTab !== 'sabr') return;
    
    setIsAnalyzing(true);
    try {
        const token = await currentUser.getIdToken();
        const res = await fetch(`${API_BASE_URL}/aiq/sabr-surface`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                spot: dSpot, time: dTime, rate: dRate/100,
                alpha: dAlpha, beta: dBeta, rho: dRho, nu: dNu
            })
        });
        const data = await res.json();
        if(data.chart_data) setSabrData(data.chart_data);
        
        setAiAnalysis("SABR Model calibrated. Observe skew impact via Rho.");
    } catch(e) {
        console.error(e);
    } finally {
        setIsAnalyzing(false);
    }
  }, [currentUser, activeTab, dSpot, dTime, dRate, dAlpha, dBeta, dRho, dNu]);

  useEffect(() => { calculateSabr(); }, [calculateSabr]);

  // 4. MATRIX GENERATION
  const generateMatrix = () => {
      setIsAnalyzing(true);
      const params = { S: spot, K: strike, T: time/365, r: rate/100, sigma: vol/100, type: optionRight.toLowerCase() as any };
      const baseRes = calculateBlackScholes(params.S, params.K, params.T, params.r, params.sigma, params.type);
      const matrix = generateSensitivityMatrix(params, baseRes.price);
      setScenarioMatrix(matrix);
      setIsAnalyzing(false);
      toast.success("Sensitivity Matrix Generated");
  };

  const handleBookButton = () => {
      if (onBookQuote && (pricingResult || ivResult)) {
          const price = pricingResult ? parseFloat(pricingResult.price) : marketPrice;
          const ticker = `${underlying} ${strike}${optionRight.charAt(0)}`;
          onBookQuote({
              side: 'Buy', assetClass: 'Equity Option', product: `${underlying} ${optionRight} ${strike}`,
              symbol: ticker, rawTicker: underlying, quantity: 1000, price: price,
              counterparty: 'Exchange', valuation: price * 1000
          });
          toast.success("Ticket sent to Operations");
      }
  };

  const getHeatmapColor = (pnl: number) => {
      if (Math.abs(pnl) < 0.01) return 'bg-slate-50 text-slate-800';
      if (pnl > 0) return pnl > 5 ? 'bg-emerald-600 text-white' : 'bg-emerald-200 text-emerald-900';
      return pnl < -5 ? 'bg-red-600 text-white' : 'bg-red-200 text-red-900';
  };

  return (
    <div className="p-6 h-full overflow-y-auto custom-scrollbar bg-slate-50">
      <div className="mb-6 flex justify-between items-end">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <IconQuant className="text-purple-600"/> Quant Pricing Engine
          </h2>
          <p className="text-slate-500 text-sm">Real-time Black-Scholes & Sensitivity Analysis.</p>
        </div>
        
        {/* IMPROVED TAB SWITCHER */}
        <div className="bg-slate-100 p-1.5 rounded-xl flex gap-2 shadow-inner">
            <button 
                onClick={() => setActiveTab('pricing')} 
                className={`flex-1 px-4 py-2 text-xs font-bold rounded-lg transition-all text-center ${
                    activeTab === 'pricing' 
                    ? 'bg-white text-blue-700 shadow-md ring-1 ring-black/5' 
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                }`}
            >
                Pricing & Greeks
            </button>
            <button 
                onClick={() => setActiveTab('matrix')} 
                className={`flex-1 px-4 py-2 text-xs font-bold rounded-lg transition-all text-center ${
                    activeTab === 'matrix' 
                    ? 'bg-white text-purple-700 shadow-md ring-1 ring-black/5' 
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                }`}
            >
                Scenario Matrix
            </button>
            <button 
                onClick={() => setActiveTab('sabr')} 
                className={`flex-1 px-4 py-2 text-xs font-bold rounded-lg transition-all text-center ${
                    activeTab === 'sabr' 
                    ? 'bg-white text-emerald-700 shadow-md ring-1 ring-black/5' 
                    : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                }`}
            >
                SABR Model
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* LEFT PANEL: INPUTS */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 text-sm uppercase tracking-wide">Parameters</h3>
            
            {activeTab === 'pricing' && (
                <div className="space-y-5">
                    {/* Improved Mode Switcher */}
                    <div className="flex p-1 bg-slate-100 rounded-lg mb-4 gap-1">
                        <button 
                            onClick={() => setCalculationMode('price')} 
                            className={`flex-1 py-2 text-xs font-bold rounded-md transition-all text-center ${
                                calculationMode === 'price' ? 'bg-white shadow text-blue-700' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Price Option
                        </button>
                        <button 
                            onClick={() => setCalculationMode('iv')} 
                            className={`flex-1 py-2 text-xs font-bold rounded-md transition-all text-center ${
                                calculationMode === 'iv' ? 'bg-white shadow text-purple-700' : 'text-slate-500 hover:text-slate-700'
                            }`}
                        >
                            Solve for IV
                        </button>
                    </div>

                    <div className="relative">
                        <input type="text" value={underlying} onChange={(e) => setUnderlying(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === 'Enter' && handleTickerSearch()} className="w-full border border-slate-300 rounded-lg p-2.5 pl-9 text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500" placeholder="TICKER" />
                        <button onClick={handleTickerSearch} disabled={isFetchingPrice} className="absolute left-3 top-3 text-slate-400">{isFetchingPrice ? <span className="animate-spin text-blue-500">↻</span> : <IconSearch size={16}/>}</button>
                    </div>

                    <div className="flex bg-slate-100 p-1 rounded-lg gap-1">
                        <button onClick={() => setOptionRight('Call')} className={`flex-1 py-2 text-xs font-bold rounded-md transition-all uppercase ${optionRight === 'Call' ? 'bg-white text-emerald-600 shadow' : 'text-slate-500 hover:text-slate-700'}`}>CALL</button>
                        <button onClick={() => setOptionRight('Put')} className={`flex-1 py-2 text-xs font-bold rounded-md transition-all uppercase ${optionRight === 'Put' ? 'bg-white text-red-600 shadow' : 'text-slate-500 hover:text-slate-700'}`}>PUT</button>
                    </div>

                    <InputSlider label="Spot Price ($)" val={spot} set={setSpot} min={10} max={500} step={1} />
                    <InputSlider label="Strike Price ($)" val={strike} set={setStrike} min={10} max={500} step={1} />
                    
                    {calculationMode === 'iv' ? (
                        <div>
                            <label className="block text-xs font-bold text-purple-700 mb-1">Market Price ($)</label>
                            <input type="number" value={marketPrice} onChange={(e) => setMarketPrice(Number(e.target.value))} className="w-full bg-white border border-purple-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-purple-500 font-mono" />
                        </div>
                    ) : (
                        <InputSlider label="Volatility (%)" val={vol} set={setVol} min={1} max={200} step={1} />
                    )}

                    <InputSlider label="Time (Days)" val={time} set={setTime} min={1} max={365} step={1} />
                    <InputSlider label="Risk Free Rate (%)" val={rate} set={setRate} min={0} max={10} step={0.1} />
                </div>
            )}

            {activeTab === 'sabr' && (
                <div className="space-y-4">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">SABR Parameters</h3>
                    <div className="p-3 bg-emerald-50 border border-emerald-100 rounded text-xs text-emerald-800 mb-2">
                        <strong>Stochastic Volatility:</strong> Adjust parameters to shape the Volatility Smile.
                    </div>
                    <InputSlider label="Alpha (ATM Vol)" val={sabrAlpha} set={setSabrAlpha} min={0.01} max={1.0} step={0.01} />
                    <InputSlider label="Beta (Elasticity)" val={sabrBeta} set={setSabrBeta} min={0} max={1} step={0.1} />
                    <InputSlider label="Rho (Skew)" val={sabrRho} set={setSabrRho} min={-0.99} max={0.99} step={0.05} />
                    <InputSlider label="Nu (Vol of Vol)" val={sabrNu} set={setSabrNu} min={0} max={2.0} step={0.1} />
                    
                    <div className="border-t border-slate-100 pt-4 mt-4 space-y-4">
                         <InputSlider label="Spot Price" val={spot} set={setSpot} min={10} max={500} step={1} />
                         <InputSlider label="Time (Days)" val={time} set={setTime} min={1} max={365} step={1} />
                    </div>
                </div>
            )}

            {activeTab === 'matrix' && (
                <div className="space-y-5">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Stress Parameters</h3>
                    <div className="p-3 bg-purple-50 border border-purple-100 rounded text-xs text-purple-800">
                        <strong>Stress Test:</strong> Simulate P&L across +/- 10% Spot and Vol moves.
                    </div>
                    <InputSlider label="Spot Price ($)" val={spot} set={setSpot} min={10} max={500} step={1} />
                    <InputSlider label="Strike Price ($)" val={strike} set={setStrike} min={10} max={500} step={1} />
                    <InputSlider label="Volatility (%)" val={vol} set={setVol} min={1} max={200} step={1} />
                    <button onClick={generateMatrix} className="w-full bg-slate-800 text-white py-3 rounded-lg font-bold text-xs mt-4 hover:bg-slate-900 transition-all shadow-md">Generate Matrix</button>
                </div>
            )}

          </div>
        </div>

        {/* RIGHT PANEL: RESULTS */}
        <div className="lg:col-span-8 space-y-6">
          
          {activeTab === 'pricing' && (
              <>
                <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 min-h-[160px] flex items-center justify-between">
                    {calculationMode === 'price' ? (
                        <div>
                            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Theoretical Value</div>
                            {isAnalyzing ? <div className="animate-pulse h-10 w-32 bg-slate-100 rounded"></div> 
                            : pricingResult ? <div className="text-5xl font-mono font-bold text-slate-800">${pricingResult.price.toFixed(2)}</div> 
                            : <div className="text-slate-300 text-xl">--</div>}
                        </div>
                    ) : (
                        <div>
                            <div className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-1">Implied Volatility</div>
                            {ivResult ? (
                                <div>
                                    <div className="text-5xl font-mono font-bold text-purple-600">{ivResult.percent}%</div>
                                    <div className="text-xs text-slate-400 mt-1">Converged in {ivResult.iterations} steps</div>
                                </div>
                            ) : <div className="text-slate-300 text-xl">--</div>}
                        </div>
                    )}

                    {(pricingResult || ivResult) && (
                        <button onClick={handleBookButton} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-bold text-sm shadow-md transition-all flex items-center gap-2">
                            <IconOps /> Book Quote
                        </button>
                    )}
                </div>

                {calculationMode === 'price' && pricingResult && (
                    <div className="grid grid-cols-5 gap-4">
                        <GreekCard label="Delta" val={pricingResult.greeks?.delta} color="text-blue-600" />
                        <GreekCard label="Gamma" val={pricingResult.greeks?.gamma} />
                        <GreekCard label="Vega" val={pricingResult.greeks?.vega} />
                        <GreekCard label="Theta" val={pricingResult.greeks?.theta} color="text-red-500" />
                        <GreekCard label="Rho" val={pricingResult.greeks?.rho} />
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 h-[250px] flex flex-col">
                        <h3 className="font-bold text-slate-700 mb-2 text-xs flex items-center gap-2"><IconTrendingUp size={14}/> P&L Payoff</h3>
                        <div className="flex-1 w-full min-h-0">
                            {pricingResult?.payoff_chart ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={pricingResult.payoff_chart} margin={{top: 5, right: 10, left: -20, bottom: 0}}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="spot" tick={{fontSize: 10}} domain={['auto','auto']} />
                                        <YAxis tick={{fontSize: 10}} />
                                        <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontSize:'11px'}} />
                                        <ReferenceLine y={0} stroke="#94a3b8" />
                                        <ReferenceLine x={spot} stroke="#f59e0b" strokeDasharray="3 3" />
                                        <Line type="monotone" dataKey="pnl" stroke={optionRight === 'Call' ? '#10b981' : '#ef4444'} strokeWidth={2} dot={false} isAnimationActive={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : <div className="flex h-full items-center justify-center text-slate-300 text-xs">Chart loading...</div>}
                        </div>
                    </div>

                    <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 h-[250px] flex flex-col">
                        <h3 className="font-bold text-slate-700 mb-2 text-xs flex items-center gap-2"><IconActivity size={14}/> Volatility Skew</h3>
                        <div className="flex-1 w-full min-h-0">
                            {volSurfaceData.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={volSurfaceData} margin={{top: 5, right: 10, left: -20, bottom: 0}}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="strike" tick={{fontSize: 10}} domain={['dataMin', 'dataMax']} />
                                        <YAxis tick={{fontSize: 10}} domain={['auto', 'auto']} />
                                        <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', fontSize:'11px'}} />
                                        <ReferenceDot x={strike} y={calculationMode === 'iv' && ivResult ? ivResult.percent : vol} r={4} fill="#ef4444" stroke="white" />
                                        <Line type="monotone" dataKey="iv" stroke="#8b5cf6" strokeWidth={2} dot={false} isAnimationActive={false} />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : <div className="flex h-full items-center justify-center text-slate-300 text-xs">Vol data loading...</div>}
                        </div>
                    </div>
                </div>
              </>
          )}

          {activeTab === 'sabr' && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 min-h-[500px]">
                <h3 className="font-bold text-slate-800 mb-6 text-lg flex items-center gap-2">
                    <IconActivity className="text-emerald-600"/> SABR Volatility Smile
                </h3>
                <div className="h-[400px]">
                    {sabrData.length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={sabrData} margin={{top: 5, right: 20, left: 0, bottom: 5}}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="strike" label={{ value: 'Strike Price', position: 'insideBottom', offset: -5 }} tick={{fontSize: 10}} />
                                <YAxis label={{ value: 'Implied Vol (%)', angle: -90, position: 'insideLeft' }} domain={['auto', 'auto']} />
                                <Tooltip contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'}} />
                                <ReferenceLine x={spot} stroke="#f59e0b" strokeDasharray="3 3" label="Spot" />
                                <Line type="monotone" dataKey="iv" stroke="#10b981" strokeWidth={3} dot={false} isAnimationActive={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    ) : <div className="flex h-full items-center justify-center text-slate-300">Loading SABR...</div>}
                </div>
            </div>
          )}

          {activeTab === 'matrix' && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 min-h-[500px]">
                  <h3 className="font-bold text-slate-800 mb-6 text-lg flex items-center gap-2">
                      <IconStrategy /> Sensitivity Matrix (P&L)
                  </h3>
                  {scenarioMatrix ? (
                      <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                              <thead>
                                  <tr>
                                      <th className="p-3 bg-slate-50 text-slate-400 text-xs border border-slate-200 uppercase font-bold text-left">Vol \ Spot</th>
                                      {[-10, -5, 0, 5, 10].map(s => (
                                          <th key={s} className="p-2 bg-slate-50 font-bold text-slate-700 border border-slate-200 w-24 text-center">{s > 0 ? '+' : ''}{s}%</th>
                                      ))}
                                  </tr>
                              </thead>
                              <tbody>
                                  {scenarioMatrix.map((row, i) => {
                                      const volLabels = ["+10% Vol", "+5% Vol", "Base Vol", "-5% Vol", "-10% Vol"];
                                      return (
                                          <tr key={i}>
                                              <td className="p-3 bg-slate-50 font-bold text-slate-600 border border-slate-200 text-left text-xs uppercase">{volLabels[i]}</td>
                                              {row.map((cell, j) => (
                                                  <td key={j} className={`p-3 text-center border border-slate-200 font-mono font-medium ${getHeatmapColor(cell.pnl)}`}>
                                                      {cell.pnl > 0 ? '+' : ''}{cell.pnl.toFixed(2)}
                                                  </td>
                                              ))}
                                          </tr>
                                      );
                                  })}
                              </tbody>
                          </table>
                      </div>
                  ) : (
                      <div className="flex flex-col items-center justify-center h-64 text-slate-400 bg-slate-50/50 rounded border border-dashed border-slate-200">
                          <p>Click "Generate Matrix" to simulate scenarios.</p>
                      </div>
                  )}
              </div>
          )}
          
          {/* AI INTELLIGENCE */}
          <div className="bg-slate-900 text-slate-200 p-6 rounded-lg shadow-md border border-slate-700 relative overflow-hidden">
             <div className="absolute top-0 right-0 p-4 opacity-10"><IconQuant /></div>
             <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
               <span className={`w-2 h-2 rounded-full ${isAnalyzing ? 'bg-blue-400 animate-pulse' : 'bg-emerald-400'}`}></span>
               AiQ Intelligence Engine
             </h3>
             <div className="text-sm leading-relaxed font-light">
               {isAnalyzing ? "Analyzing scenario..." : aiAnalysis || <p className="text-slate-500 italic">Ready to analyze scenarios.</p>}
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const InputSlider = ({ label, val, set, min, max, step }: any) => (
    <div>
        <div className="flex justify-between mb-1">
            <label className="text-[10px] font-bold text-slate-500 uppercase">{label}</label>
            <span className="text-xs font-mono font-bold text-slate-700">{val}</span>
        </div>
        <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => set(Number(e.target.value))} className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
    </div>
);

const GreekCard = ({ label, val, color = 'text-slate-800' }: any) => (
    <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-center">
        <div className="text-[10px] font-bold text-slate-400 uppercase mb-1">{label}</div>
        <div className={`text-lg font-mono font-bold ${color}`}>{val !== undefined ? val.toFixed(3) : '-'}</div>
    </div>
);

export default QuantPricingView;